import type { MapPlacePreview } from '@fontbold/ui';
import { graphql } from '#/lib/gql';
import { fetchGraphQL } from '#/lib/graphql';
import { slugToLabel } from '#/lib/location';

const PREVIEW_QUERY = graphql(`
  query MapPreview($spaceId: String!, $slug: String!) {
    place(spaceId: $spaceId) {
      getBySlug(slug: $slug, surface: DetailPage) {
        description
        citySlug
        districtSlug
        labels
        cover {
          cardUrl
        }
      }
    }
  }
`);

const cache = new Map<string, MapPlacePreview | null>();

function slugFromHref(href: string): string | null {
  return href.split('/').filter(Boolean).at(-1) ?? null;
}

export async function loadMapPreview(
  spaceId: string,
  href: string,
): Promise<MapPlacePreview | null> {
  const slug = slugFromHref(href);
  if (!slug) return null;

  const key = `${spaceId}:${slug}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const data = await fetchGraphQL(PREVIEW_QUERY, { spaceId, slug });
  const node = data?.place?.getBySlug;
  const preview: MapPlacePreview | null = node
    ? {
        cover: node.cover?.cardUrl ?? null,
        description: node.description,
        location: [node.districtSlug, node.citySlug]
          .filter(Boolean)
          .map(slugToLabel)
          .join(' - '),
        labels: (node.labels ?? []).filter((label): label is string =>
          Boolean(label),
        ),
      }
    : null;

  cache.set(key, preview);
  return preview;
}
